import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Timeline } from "@/components/Timeline";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Send, Clapperboard, PackageCheck } from "lucide-react";

export default function AdminProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [deliveryUrl, setDeliveryUrl] = useState("");

  const load = async () => {
    const r = await api.get(`/projects/${id}`);
    setProject(r.data);
    setPrice(r.data.price_total || "");
    setNote(r.data.quote_note || "");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!project) return <div className="max-w-4xl mx-auto px-6 py-12 text-white/40 animate-pulse">Yükleniyor…</div>;

  const action = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); setProject(r.data); toast.success(msg); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const sendQuote = () => {
    if (!price || Number(price) <= 0) return toast.error("Geçerli bir fiyat girin");
    action(() => api.post(`/projects/${id}/quote`, { price_total: Number(price), note }), "Teklif gönderildi");
  };
  const startProduction = () => action(() => api.post(`/projects/${id}/production`), "Proje üretime alındı");
  const deliver = () => action(() => api.post(`/projects/${id}/deliver`, { delivery_note: deliveryNote, delivery_url: deliveryUrl }), "Proje teslim edildi");

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <button onClick={() => navigate("/admin")} data-testid="admin-back-button"
        className="flex items-center gap-2 text-white/50 hover:text-gold transition-colors mb-8 text-sm">
        <ArrowLeft className="w-4 h-4" /> Yönetim paneline dön
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl lg:text-5xl mb-2">{project.title}</h1>
          <div className="flex items-center gap-3">
            <StatusBadge status={project.status} testid="admin-detail-status" />
            <span className="text-white/50 text-sm">{project.customer_name} • {project.customer_email}</span>
          </div>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full border ${project.payment_status === "PAID" ? "text-emerald-300 border-emerald-500/40" : "text-white/40 border-white/15"}`}>
          Ödeme: {project.payment_status}
        </span>
      </div>

      <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6 mb-8">
        <Timeline status={project.status} />
      </div>

      <section className="mb-6">
        <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Müşteri Fikri</p>
        <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6 text-white/80 whitespace-pre-wrap leading-relaxed">{project.idea}</div>
      </section>

      {project.screenplay && (
        <section className="mb-8">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Onaylanan Senaryo</p>
          <div className="bg-[#121212] border border-white/10 rounded-xl p-6 text-white/80 whitespace-pre-wrap font-mono text-sm leading-relaxed max-h-96 overflow-y-auto">
            {project.screenplay}
          </div>
        </section>
      )}

      {/* Quote panel */}
      {(project.status === "approved" || project.status === "quoted") && (
        <section className="bg-[#0A0A0A] border border-gold/30 rounded-xl p-7 mb-8" data-testid="admin-quote-panel">
          <p className="text-xs uppercase tracking-widest text-gold mb-4">Maliyet & Teklif</p>
          <div className="space-y-4">
            <div>
              <Label className="text-white/70">Toplam Bütçe (₺)</Label>
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)}
                data-testid="quote-price-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold h-11" placeholder="Örn: 50000" />
              {price > 0 && <p className="text-sm text-gold mt-1">%50 Ön Ödeme: {(Number(price) / 2).toLocaleString("tr-TR")} ₺</p>}
            </div>
            <div>
              <Label className="text-white/70">Teklif Notu</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                data-testid="quote-note-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold resize-none"
                placeholder="Teslim süresi, kapsam, ödeme koşulları…" />
            </div>
            <Button onClick={sendQuote} disabled={busy} data-testid="send-quote-button"
              className="bg-gold text-black hover:bg-gold-light rounded-md h-11 px-6">
              <Send className="w-4 h-4 mr-2" /> {project.status === "quoted" ? "Teklifi Güncelle" : "Teklifi Gönder"}
            </Button>
          </div>
        </section>
      )}

      {project.status === "paid" && (
        <section className="bg-[#0A0A0A] border border-emerald-500/30 rounded-xl p-7 mb-8">
          <p className="text-emerald-300 mb-4">✓ Ön ödeme alındı ({project.deposit?.toLocaleString("tr-TR")} ₺). Projeyi üretime alabilirsiniz.</p>
          <Button onClick={startProduction} disabled={busy} data-testid="start-production-button"
            className="bg-gold text-black hover:bg-gold-light rounded-md h-11 px-6">
            <Clapperboard className="w-4 h-4 mr-2" /> Üretime Al
          </Button>
        </section>
      )}

      {project.status === "in_production" && (
        <section className="bg-[#0A0A0A] border border-white/10 rounded-xl p-7 mb-8" data-testid="deliver-panel">
          <p className="text-xs uppercase tracking-widest text-gold mb-4">Teslimat</p>
          <div className="space-y-4">
            <div>
              <Label className="text-white/70">Teslim Notu</Label>
              <Textarea value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} rows={3}
                data-testid="delivery-note-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold resize-none"
                placeholder="Teslimat detayları, kalan %50 ödeme bilgisi…" />
            </div>
            <div>
              <Label className="text-white/70">Teslimat Bağlantısı (opsiyonel)</Label>
              <Input value={deliveryUrl} onChange={(e) => setDeliveryUrl(e.target.value)}
                data-testid="delivery-url-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold h-11"
                placeholder="https://…" />
            </div>
            <Button onClick={deliver} disabled={busy} data-testid="deliver-button"
              className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-md h-11 px-6">
              <PackageCheck className="w-4 h-4 mr-2" /> Teslim Et
            </Button>
          </div>
        </section>
      )}

      {project.status === "delivered" && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-emerald-300">
          ✓ Proje teslim edildi.
        </div>
      )}
    </div>
  );
}
